import React, { forwardRef, useMemo, useState } from 'react';
import { CheckCircle, Loader2, Send, Sparkles } from 'lucide-react';
import { useI18n } from '../i18n';
import apiService from '../services/api';
import { getClientId } from '../services/clientId';
import { getSessionEmail } from '../services/ltiSession';

const SUBMITTED_PREFIX = 'survey_submitted:';

const UEQ_ITEMS = [
    { key: 'obstructive_supportive', leftKey: 'ueqObstructive', rightKey: 'ueqSupportive' },
    { key: 'complicated_easy', leftKey: 'ueqComplicated', rightKey: 'ueqEasy' },
    { key: 'inefficient_efficient', leftKey: 'ueqInefficient', rightKey: 'ueqEfficient' },
    { key: 'confusing_clear', leftKey: 'ueqConfusing', rightKey: 'ueqClear' },
    { key: 'boring_exciting', leftKey: 'ueqBoring', rightKey: 'ueqExciting' },
    { key: 'notInteresting_interesting', leftKey: 'ueqNotInteresting', rightKey: 'ueqInteresting' },
    { key: 'conventional_innovative', leftKey: 'ueqConventional', rightKey: 'ueqInnovative' },
    { key: 'unattractive_attractive', leftKey: 'ueqUnattractive', rightKey: 'ueqAttractive' },
];

const LIKERT_ITEMS = [
    { key: 'realism', labelKey: 'likertRealism', categoryKey: 'catCredibility' },
    { key: 'feedback_useful', labelKey: 'likertFeedback', categoryKey: 'catCredibility' },
    { key: 'comfort', labelKey: 'likertComfort', categoryKey: 'catSafety' },
    { key: 'tone', labelKey: 'likertTone', categoryKey: 'catSafety' },
    { key: 'trust', labelKey: 'likertTrust', categoryKey: 'catTrust' },
];

const AGE_RANGES = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];

const SENIORITY_LEVELS = [
    { key: 'junior', labelKey: 'seniorityJunior' },
    { key: 'mid', labelKey: 'seniorityMid' },
    { key: 'senior', labelKey: 'senioritySenior' },
    { key: 'lead', labelKey: 'seniorityLead' },
];

const SECTORS = [
    { key: 'tech', labelKey: 'sectorTech' },
    { key: 'finance', labelKey: 'sectorFinance' },
    { key: 'industry', labelKey: 'sectorIndustry' },
    { key: 'public', labelKey: 'sectorPublic' },
    { key: 'commerce', labelKey: 'sectorCommerce' },
    { key: 'other', labelKey: 'sectorOther' },
];

function PillButton({ selected, disabled, onClick, children }) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                selected
                    ? 'text-white border-transparent shadow-sm'
                    : 'text-stone-600 border-stone-300 bg-white hover:border-stone-400'
            } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
            style={selected ? { background: '#1A4731' } : {}}
        >
            {children}
        </button>
    );
}

function ScaleRow({ value, onChange, disabled, points, accent = '#1A4731' }) {
    return (
        <div className="flex items-center justify-center gap-1 sm:gap-1.5">
            {Array.from({ length: points }, (_, i) => i + 1).map((n) => {
                const active = value === n;
                return (
                    <button
                        key={n}
                        type="button"
                        disabled={disabled}
                        onClick={() => !disabled && onChange(n)}
                        aria-label={`${n}`}
                        className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border text-xs font-semibold transition-all ${
                            active
                                ? 'text-white border-transparent scale-110 shadow-sm'
                                : 'text-stone-500 border-stone-300 bg-white hover:border-stone-400'
                        } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                        style={active ? { background: accent } : {}}
                    >
                        {n}
                    </button>
                );
            })}
        </div>
    );
}

const FeedbackSurvey = forwardRef(function FeedbackSurvey(
    { threadId, locale, metadata, onSubmitted, highlight = false },
    ref,
) {
    const { t } = useI18n();
    const clientId = useMemo(() => getClientId(), []);
    const submittedKey = threadId ? `${SUBMITTED_PREFIX}${threadId}` : null;

    const [ueq, setUeq] = useState({});
    const [likert, setLikert] = useState({});
    const [openText, setOpenText] = useState('');
    const [ageRange, setAgeRange] = useState('');
    const [country, setCountry] = useState('');
    const [role, setRole] = useState('');
    const [seniority, setSeniority] = useState('');
    const [sectors, setSectors] = useState([]);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(() => {
        if (!submittedKey) return false;
        try { return window.localStorage.getItem(submittedKey) === '1'; } catch { return false; }
    });

    const setUeqValue = (k, v) => setUeq((prev) => ({ ...prev, [k]: v }));
    const setLikertValue = (k, v) => setLikert((prev) => ({ ...prev, [k]: v }));
    const toggleSector = (k) => setSectors((prev) =>
        prev.includes(k) ? prev.filter((s) => s !== k) : [...prev, k]
    );

    if (!threadId) return null;

    if (submitted) {
        return (
            <section ref={ref} className="mt-6 rounded-2xl border-2 p-6 flex items-start gap-3" style={{ borderColor: '#1A4731', background: '#F0FDF4' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#1A4731' }}>
                    <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                    <h3 className="text-base font-semibold text-stone-800">{t('surveyThanksTitle')}</h3>
                    <p className="text-sm text-stone-600 mt-1 leading-relaxed">{t('surveyThanksMessage')}</p>
                </div>
            </section>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const ueqMissing = UEQ_ITEMS.some((it) => !ueq[it.key]);
        if (ueqMissing) {
            setError(t('surveyUeqRequired'));
            return;
        }
        const likertMissing = LIKERT_ITEMS.some((it) => !likert[it.key]);
        if (likertMissing) {
            setError(t('surveyLikertRequired'));
            return;
        }
        const countryTrim = country.trim();
        const roleTrim = role.trim();
        if (!ageRange || !countryTrim || !roleTrim || !seniority || sectors.length === 0) {
            setError(t('surveyDemographicsRequired'));
            return;
        }
        // Email: viene del JWT LTI (source of truth). Si Moodle no lo libera,
        // se envia vacio — el backend tambien valida claims y rellena su lado.
        const emailTrim = (getSessionEmail() || '').trim();

        setIsSubmitting(true);
        try {
            const demographics = {
                age_range: ageRange,
                country: countryTrim,
                role: roleTrim,
                seniority,
                sectors,
            };
            const openTextTrim = openText.trim();
            await apiService.submitFeedbackSurvey({
                thread_id: threadId,
                client_id: clientId,
                ueq,
                likert,
                open_text: openTextTrim,
                comment: openTextTrim,
                email: emailTrim,
                demographics,
                locale: locale || 'es',
                // Duplicamos las nuevas estructuras dentro de `metadata` por
                // si el backend tiene un schema estricto que descarta campos
                // desconocidos en la raíz (caso reportado en la tabla admin
                // con columnas vacías). El admin lee de ambos sitios.
                metadata: {
                    ...(metadata || {}),
                    ueq,
                    likert,
                    demographics,
                    open_text: openTextTrim,
                },
            });
            try { if (submittedKey) window.localStorage.setItem(submittedKey, '1'); } catch {}
            setSubmitted(true);
            onSubmitted?.();
        } catch (err) {
            console.error('Error enviando encuesta:', err);
            setError(t('surveyError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section
            ref={ref}
            className={`mt-6 rounded-2xl overflow-hidden border-2 shadow-sm transition-shadow duration-700 ${highlight ? 'survey-highlight' : ''}`}
            style={{ borderColor: '#1A4731' }}
        >
            {/* Banner invitación */}
            <div
                className="px-6 py-5 text-white"
                style={{ background: 'linear-gradient(135deg, #1A4731 0%, #2D7A4F 100%)' }}
            >
                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                        <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold leading-tight">{t('surveyTitle')}</h3>
                        <p className="text-sm text-white/85 mt-1.5 leading-relaxed">{t('surveySubtitle')}</p>
                    </div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md self-start" style={{ background: '#FEF3C7', color: '#92400E' }}>
                        {t('surveyRequiredBadge')}
                    </span>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white px-6 py-6">

                <div className="space-y-8">
                    {/* Sección 1 — UEQ */}
                    <fieldset>
                        <legend className="text-sm font-semibold text-stone-800 mb-1">
                            {t('surveySection1Title')}
                        </legend>
                        <p className="text-xs text-stone-500 mb-3 leading-relaxed">
                            {t('surveySection1Instructions')}
                        </p>
                        <div className="text-[11px] text-stone-500 mb-4 space-y-0.5">
                            <p>{t('surveyUeqScale1')}</p>
                            <p>{t('surveyUeqScale7')}</p>
                        </div>

                        <div className="space-y-4">
                            {UEQ_ITEMS.map((item) => (
                                <div key={item.key} className="grid grid-cols-[minmax(80px,1fr)_auto_minmax(80px,1fr)] items-center gap-2 sm:gap-3">
                                    <span className="text-xs sm:text-sm text-stone-600 text-right truncate">
                                        {t(item.leftKey)}
                                    </span>
                                    <ScaleRow
                                        value={ueq[item.key]}
                                        onChange={(v) => setUeqValue(item.key, v)}
                                        disabled={isSubmitting}
                                        points={7}
                                    />
                                    <span className="text-xs sm:text-sm text-stone-600 text-left truncate">
                                        {t(item.rightKey)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </fieldset>

                    <hr className="border-stone-200" />

                    {/* Sección 2 — Likert 1–5 */}
                    <fieldset>
                        <legend className="text-sm font-semibold text-stone-800 mb-1">
                            {t('surveySection2Title')}
                        </legend>
                        <p className="text-xs text-stone-500 mb-4 leading-relaxed">
                            {t('surveySection2Instructions')}
                        </p>

                        <div className="space-y-5">
                            {LIKERT_ITEMS.map((item) => (
                                <div key={item.key}>
                                    <p className="text-sm text-stone-700 leading-snug mb-2">{t(item.labelKey)}</p>
                                    <div className="flex items-center justify-center gap-3">
                                        <span className="text-[10px] text-stone-400 hidden sm:inline w-24 text-right">
                                            {t('likertDisagree')}
                                        </span>
                                        <ScaleRow
                                            value={likert[item.key]}
                                            onChange={(v) => setLikertValue(item.key, v)}
                                            disabled={isSubmitting}
                                            points={5}
                                        />
                                        <span className="text-[10px] text-stone-400 hidden sm:inline w-24 text-left">
                                            {t('likertAgree')}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </fieldset>

                    <hr className="border-stone-200" />

                    {/* Sección 3 — Pregunta abierta */}
                    <div>
                        <label className="block text-sm font-semibold text-stone-800 mb-2">
                            {t('surveySection3Title')}
                        </label>
                        <textarea
                            value={openText}
                            onChange={(e) => setOpenText(e.target.value)}
                            placeholder={t('surveyOpenTextPlaceholder')}
                            rows={4}
                            disabled={isSubmitting}
                            className="w-full text-sm border border-stone-300 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-600 resize-none"
                        />
                    </div>

                    <hr className="border-stone-200" />

                    {/* Sección 4 — Sobre ti (demografía) */}
                    <fieldset>
                        <legend className="text-sm font-semibold text-stone-800 mb-1">
                            {t('surveySection4Title')}
                        </legend>
                        <p className="text-xs text-stone-500 mb-4 leading-relaxed">
                            {t('surveySection4Instructions')}
                        </p>

                        <div className="space-y-5">
                            {/* Edad */}
                            <div>
                                <label className="block text-sm text-stone-700 mb-2">
                                    {t('demoAgeLabel')}<span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {AGE_RANGES.map((r) => (
                                        <PillButton
                                            key={r}
                                            selected={ageRange === r}
                                            disabled={isSubmitting}
                                            onClick={() => setAgeRange(r)}
                                        >
                                            {r}
                                        </PillButton>
                                    ))}
                                </div>
                            </div>

                            {/* País / Región */}
                            <div>
                                <label className="block text-sm text-stone-700 mb-1.5">
                                    {t('demoCountryLabel')}<span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                    placeholder={t('demoCountryPlaceholder')}
                                    disabled={isSubmitting}
                                    className="w-full text-sm border border-stone-300 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-600"
                                />
                            </div>

                            {/* Rol actual */}
                            <div>
                                <label className="block text-sm text-stone-700 mb-1.5">
                                    {t('demoRoleLabel')}<span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    placeholder={t('demoRolePlaceholder')}
                                    disabled={isSubmitting}
                                    className="w-full text-sm border border-stone-300 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-600"
                                />
                                <p className="text-[11px] text-stone-400 mt-1">{t('demoRoleHint')}</p>
                            </div>

                            {/* Seniority */}
                            <div>
                                <label className="block text-sm text-stone-700 mb-2">
                                    {t('demoSeniorityLabel')}<span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {SENIORITY_LEVELS.map((s) => (
                                        <PillButton
                                            key={s.key}
                                            selected={seniority === s.key}
                                            disabled={isSubmitting}
                                            onClick={() => setSeniority(s.key)}
                                        >
                                            {t(s.labelKey)}
                                        </PillButton>
                                    ))}
                                </div>
                            </div>

                            {/* Sectores (multi) */}
                            <div>
                                <label className="block text-sm text-stone-700 mb-1">
                                    {t('demoSectorsLabel')}<span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <p className="text-[11px] text-stone-400 mb-2">{t('demoSectorsHint')}</p>
                                <div className="flex flex-wrap gap-2">
                                    {SECTORS.map((s) => (
                                        <PillButton
                                            key={s.key}
                                            selected={sectors.includes(s.key)}
                                            disabled={isSubmitting}
                                            onClick={() => toggleSector(s.key)}
                                        >
                                            {t(s.labelKey)}
                                        </PillButton>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    {error && (
                        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
                    )}

                    <div className="flex justify-end pt-1">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                            style={{ background: '#1A4731' }}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('surveySubmitting')}
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    {t('surveySubmit')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </form>
        </section>
    );
});

export default FeedbackSurvey;
